import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import AuctionCard from './AuctionCard';
import CreateAuction from './CreateAuction';
import AdminPanel from './AdminPanel';
import CardGallery from './CardGallery';
import MyCards from './MyCards';
import Trades from './Trades';
import Social from './Social';
import Tournament from './Tournament';
import FullscreenImageModal from './FullscreenImageModal';
import AuctionWinnerOverlay from './AuctionWinnerOverlay';
import { API_URL } from '../config';

function Dashboard({ user, token, onLogout, setUser }) {
  const [auctions, setAuctions] = useState([]);
  const [scheduledAuctions, setScheduledAuctions] = useState([]);
  const [endedAuctions, setEndedAuctions] = useState([]);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('gallery');
  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [fullscreenImage, setFullscreenImage] = useState({ isOpen: false, imageUrl: '', cardName: '' });
  const [winnerAuction, setWinnerAuction] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const newSocket = io(API_URL, {
      auth: { token }
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Connected to server');
    });

    newSocket.on('auction_created', (auction) => {
      setAuctions(prev => [auction, ...prev]);
    });

    newSocket.on('auction_scheduled', (auction) => {
      setScheduledAuctions(prev => [auction, ...prev]);
    });

    newSocket.on('auction_started', (auction) => {
      setScheduledAuctions(prev => prev.filter(a => a.id !== auction.id));
      setAuctions(prev => [auction, ...prev]);
    });

    newSocket.on('bid_placed', (auctionData) => {
      setAuctions(prev => prev.map(auction =>
          auction.id === auctionData.id ? auctionData : auction
      ));
    });

    newSocket.on('auction_ended', (auction) => {
      setAuctions(prev => prev.filter(a => a.id !== auction.id));
      setEndedAuctions(prev => [auction, ...prev]);
      setWinnerAuction(auction);
    });

    newSocket.on('auction_cancelled', (auction) => {
      setAuctions(prev => prev.filter(a => a.id !== auction.id));
      setScheduledAuctions(prev => prev.filter(a => a.id !== auction.id));
    });

    return () => {
      newSocket.close();
    };
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('announcement_updated', (data) => {
      setAnnouncement(data.text || '');
    });
    socket.on('gallery_updated', (data) => {
      const enabled = !!data.gallery_enabled;
      setGalleryEnabled(enabled);
      if (!enabled && !user.is_admin) {
        setActiveTab(prev => prev === 'gallery' ? 'mine' : prev);
      }
    });
    socket.on('trade_updated', (data) => {
      const me = data.users?.find(u => Number(u.id) === Number(user.id));
      if (me) {
        setUser(prev => {
          const next = { ...prev, patapizze: me.patapizze };
          localStorage.setItem('user', JSON.stringify(next));
          return next;
        });
      }
    });
    return () => {
      socket.off('announcement_updated');
      socket.off('gallery_updated');
      socket.off('trade_updated');
    };
  }, [socket]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [auctionsRes, scheduledRes, endedRes, galleryRes] = await Promise.all([
        axios.get(`${API_URL}/api/auctions/active`),
        axios.get(`${API_URL}/api/auctions/scheduled`),
        axios.get(`${API_URL}/api/auctions/ended`),
        axios.get(`${API_URL}/api/settings/gallery`)
      ]);

      setAuctions(auctionsRes.data);
      setScheduledAuctions(scheduledRes.data);
      setEndedAuctions(endedRes.data);
      const enabled = galleryRes.data.gallery_enabled !== false;
      setGalleryEnabled(enabled);
      if (!enabled && !user.is_admin) {
        setActiveTab(prev => prev === 'gallery' ? 'mine' : prev);
      }
    } catch (error) {
      setError('Errore nel caricamento dei dati');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncement = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/announcement`);
      setAnnouncement(res.data.text || '');
    } catch (err) {
      console.error("Error fetching announcement:", err);
    }
  };

  const handleBidSuccess = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const openFullscreenImage = (imageUrl, cardName) => {
    setFullscreenImage({ isOpen: true, imageUrl, cardName });
  };

  const closeFullscreenImage = () => {
    setFullscreenImage({ isOpen: false, imageUrl: '', cardName: '' });
  };

  if (loading) {
    return (
        <div className="container">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Caricamento aste...</h2>
          </div>
        </div>
    );
  }

  return (
      <>
        {announcement && (
            <div className="announcement-bar">
              <marquee behavior="scroll" direction="left" scrollamount="5">
                {announcement}
              </marquee>
            </div>
        )}
        <div className="container">
          <div className="header">
            <h1>🃏 Fantavaro Aste</h1>
            <p>Asta delle vere carte <b style={{ color: '#FFD700' }}>AVARE</b></p>
          </div>

          <div className="user-info">
            <div>
              <strong style={{ color: '#ffe800' }}>Ciao <span>Gay</span>!</strong>
              <p>Sei tu {user.username}?</p>
              {user.is_admin && <span className="admin-badge">ADMIN</span>}
            </div>
            <div className="patapizza-display">
              <span className="patapizza-icon"></span>
              {(user.patapizze || 0).toLocaleString()} {(user.patapizze || 0) === 1 ? 'patapizza' : 'patapizze'}
            </div>
            <button className="btn btn-secondary" onClick={onLogout}>
              Logout
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="tab-navigation">
            <button
                className={`tab-btn ${activeTab === 'mine' ? 'active' : ''}`}
                onClick={() => setActiveTab('mine')}
            >
              🃏 Le mie carte
            </button>
            <button
                className={`tab-btn ${activeTab === 'trades' ? 'active' : ''}`}
                onClick={() => setActiveTab('trades')}
            >
              🔄 Scambi
            </button>
            {(galleryEnabled || user.is_admin) && (
            <button
                className={`tab-btn ${activeTab === 'gallery' ? 'active' : ''}`}
                onClick={() => setActiveTab('gallery')}
            >
              📚 Raccolta Carte
            </button>
            )}
            <button
                className={`tab-btn ${activeTab === 'auctions' ? 'active' : ''}`}
                onClick={() => setActiveTab('auctions')}
            >
              🔥 Aste Attive
            </button>
            <button
                className={`tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`}
                onClick={() => setActiveTab('scheduled')}
            >
              ⏰ Aste Programmate
            </button>
            <button
                className={`tab-btn ${activeTab === 'ended' ? 'active' : ''}`}
                onClick={() => setActiveTab('ended')}
            >
              📋 Aste Concluse
            </button>
            <button
                className={`tab-btn ${activeTab === 'social' ? 'active' : ''}`}
                onClick={() => setActiveTab('social')}
            >
              🏆 Classifica
            </button>
            <button
                className={`tab-btn ${activeTab === 'tournament' ? 'active' : ''}`}
                onClick={() => setActiveTab('tournament')}
            >
              🏟️ Torneo
            </button>
            {user.is_admin && (
                <button
                    className={`tab-btn ${activeTab === 'admin' ? 'active' : ''}`}
                    onClick={() => setActiveTab('admin')}
                >
                  🛠️ Admin
                </button>
            )}
          </div>

          {activeTab === 'auctions' && (
              <>
                <h2>Aste Attive</h2>
                {auctions.length === 0 ? (
                    <div className="no-auctions">
                      <p>Nessuna asta attiva ora. è da aspetta lvecchio</p>
                    </div>
                ) : (
                    <div className="auctions-grid">
                      {auctions.map(auction => (
                          <AuctionCard
                              key={auction.id}
                              auction={auction}
                              user={user}
                              token={token}
                              socket={socket}
                              onBidSuccess={handleBidSuccess}
                          />
                      ))}
                    </div>
                )}
              </>
          )}

          {activeTab === 'scheduled' && (
              <>
                <h2>Aste Programmate</h2>
                {scheduledAuctions.length === 0 ? (
                    <div className="no-auctions">
                      <p>Nessuna asta programmata ancora. Aspè n attimo</p>
                    </div>
                ) : (
                    <div className="cards-clean-grid">
                      {scheduledAuctions.map(auction => {
                        return (
                            <div key={auction.id} className="scheduled-auction-clean">
                              {auction.jpeg_filename && (
                                  <img
                                      src={`${API_URL}/cards/${auction.jpeg_filename}`}
                                      alt={auction.name}
                                      className="card-clean-image"
                                  />
                              )}
                              <div className="scheduled-overlay">
                                <h3>{auction.name}</h3>
                                <p><strong>Inizio:</strong> {new Date(auction.start_time).toLocaleString('it-IT')}</p>
                                <p><strong>Offerta:</strong> <span className="patapizza-icon"></span> {auction.starting_bid.toLocaleString()}</p>
                              </div>
                            </div>
                        );
                      })}
                    </div>
                )}
              </>
          )}

          {activeTab === 'ended' && (
              <>
                <h2>📋 Aste Concluse</h2>
                {endedAuctions.length === 0 ? (
                    <div className="no-auctions">
                      <p>Nessuna asta conclusa... ancora. gobbo scemo</p>
                    </div>
                ) : (
                    <div className="ended-auctions-table">
                      <table className="auctions-history-table">
                        <thead>
                        <tr>
                          <th>Carta</th>
                          <th>Immagine</th>
                          <th>Offerta Iniziale</th>
                          <th>Offerta Finale</th>
                          <th>Vincitore</th>
                          <th>Data Fine</th>
                        </tr>
                        </thead>
                        <tbody>
                        {endedAuctions.map(auction => (
                            <tr key={auction.id}>
                              <td>
                                <strong>{auction.name}</strong>
                              </td>
                              <td>
                                {auction.jpeg_filename && (
                                    <img
                                        src={`${API_URL}/cards/${auction.jpeg_filename}`}
                                        alt={auction.name}
                                        className="auction-history-image card-clickable"
                                        onClick={() => openFullscreenImage(`${API_URL}/cards/${auction.jpeg_filename}`, auction.name)}
                                    />
                                )}
                              </td>
                              <td>
                                <span className="patapizza-icon"></span> {auction.starting_bid.toLocaleString()}
                              </td>
                              <td>
                                <span className="patapizza-icon"></span> {auction.current_bid.toLocaleString()}
                              </td>
                              <td>
                                {auction.winner_username ? (
                                    <span className="winner">{auction.winner_username}</span>
                                ) : (
                                    <span className="no-winner">Nessun vincitore</span>
                                )}
                              </td>
                              <td>
                                {new Date(auction.end_time).toLocaleString('it-IT')}
                              </td>
                            </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                )}
              </>
          )}

          {activeTab === 'mine' && (
              <MyCards user={user} />
          )}

          {activeTab === 'trades' && (
              <Trades user={user} token={token} socket={socket} />
          )}

           {activeTab === 'gallery' && (galleryEnabled || user.is_admin) && (
              <CardGallery user={user} token={token} />
          )}

          {activeTab === 'social' && (
              <Social user={user} token={token} />
          )}

          {activeTab === 'tournament' && (
              <Tournament user={user} token={token} />
          )}

          {activeTab === 'admin' && user.is_admin && (
              <AdminPanel user={user} token={token} />
          )}

          <FullscreenImageModal
              isOpen={fullscreenImage.isOpen}
              imageUrl={fullscreenImage.imageUrl}
              cardName={fullscreenImage.cardName}
              onClose={closeFullscreenImage}
          />
          {winnerAuction && (
            <AuctionWinnerOverlay
              auction={winnerAuction}
              user={user}
              onClose={() => setWinnerAuction(null)}
            />
          )}
        </div>
      </>
  );
}

export default Dashboard;